import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { archiveApi, type ArchiveCompanyView, type ArchiveDeletionView } from '../../api/client';
import { Button, Card, Chip, Empty, Kpi, LoadError, Loading, Notice, PageHeader, SelectField } from '../../components/ui';
import { dateOnly, plural, shortWhen, sizeOf } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → Archive (Storage A3, 1.13.0, Teampace 3086; help: staff-archive): the archive company by
 * company. Where each one's archive goes (Stitch's copy, and its own storage: provider, folder, readable
 * or sealed), how it's doing, how much is waiting and what last went wrong; then deletion runs, legal
 * holds and archive-only companies across every company. Read only. Platform-wide health (the archive
 * service, its passes, the bucket) is on System → Archive health.
 */

const FILTERS = [
  { value: '', label: 'Every company' },
  { value: 'failing', label: 'Failing' },
  { value: 'behind', label: 'Queue over 1 day' },
  { value: 'own', label: 'With their own storage' },
  { value: 'sealed', label: 'Sealed copies' },
  { value: 'archive_only', label: 'Archive only' }
];

const HEALTH: Record<ArchiveCompanyView['health'], { tone: 'ok' | 'brand' | 'warn' | 'crit'; label: string }> = {
  ok: { tone: 'ok', label: 'Up to date' },
  waiting: { tone: 'brand', label: 'Archiving' },
  behind: { tone: 'warn', label: 'Behind' },
  failing: { tone: 'crit', label: 'Failing' }
};

const PROVIDERS: Record<string, string> = {
  aws: 'Amazon S3',
  wasabi: 'Wasabi',
  b2: 'Backblaze B2',
  r2: 'Cloudflare R2',
  spaces: 'DigitalOcean Spaces',
  linode: 'Akamai (Linode)',
  minio: 'MinIO',
  other: 'S3-compatible',
  gcs: 'Google Cloud Storage',
  azure: 'Azure Blob Storage'
};

const KINDS: Record<string, string> = {
  calls: 'Call records',
  recordings: 'Recordings',
  voicemail: 'Voicemail',
  messages: 'Messages and texts',
  transcripts: 'Transcripts',
  patches: 'Patches',
  meetings: 'Meetings'
};

function deletionState(d: ArchiveDeletionView): { tone: 'ok' | 'brand' | 'warn' | 'crit' | 'neutral'; label: string } {
  if (d.deleteState === 'done') return { tone: 'ok', label: 'Deleted' };
  if (d.deleteState === 'failed') return { tone: 'crit', label: 'Failed' };
  if (d.deleteState === 'skipped') return { tone: 'neutral', label: 'Skipped' };
  if (d.outcome === 'to_confirm') return { tone: 'warn', label: 'Waiting for the company’s admin' };
  if (d.outcome === 'confirmed') return { tone: 'brand', label: 'Confirmed, to delete' };
  if (d.outcome === 'held') return { tone: 'neutral', label: 'On hold' };
  return { tone: 'neutral', label: d.outcome };
}

export function AdminArchiveCompanies() {
  const data = useData(() => archiveApi.companies(), []);
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const v = data.data;
  const shown = useMemo(() => {
    const text = q.trim().toLowerCase();
    return (v?.companies ?? []).filter((c) => {
      if (text && !`${c.name} ${c.domain ?? ''}`.toLowerCase().includes(text)) return false;
      if (filter === 'failing') return c.health === 'failing';
      if (filter === 'behind') return c.health === 'behind';
      if (filter === 'own') return Boolean(c.own);
      if (filter === 'sealed') return c.own?.mode === 'sealed';
      if (filter === 'archive_only') return c.status === 'archive_only';
      return true;
    });
  }, [v, filter, q]);
  const count = (h: ArchiveCompanyView['health']) => (v?.companies ?? []).filter((c) => c.health === h).length;

  return (
    <>
      <PageHeader
        eyebrow="Every company"
        title="Archive"
        actions={
          <Button variant="ghost" icon="refresh" onClick={data.reload}>
            Refresh
          </Button>
        }
      />
      <div className="content">
        <p className="muted small">
          Each company’s archive: where it goes, how far behind it is, and what last went wrong. The archive service itself (answering, its passes, Stitch’s bucket) is on{' '}
          <Link to="/admin/system#archive-health">System → Archive health</Link>.
        </p>
        {data.error ? (
          <Card>
            <LoadError error={data.error} retry={data.reload} />
          </Card>
        ) : !v ? (
          <Card>
            <Loading label="Loading the archive" />
          </Card>
        ) : (
          <>
            <section aria-label="Archive by company" className="kpis">
              <Kpi label="Companies" value={v.companies.length} foot={`${plural(v.companies.filter((c) => c.own).length, 'with its own storage', 'with their own storage')}`} />
              <Kpi label="Failing" value={count('failing')} foot="Stopped items, or their own storage not answering" />
              <Kpi label="Behind" value={count('behind')} foot="Waiting over a day" />
              <Kpi label="Archive only" value={v.archiveOnly.length} foot={plural(v.holds.length, 'legal hold on', 'legal holds on')} />
            </section>
            <Card
              title="Companies"
              action={
                <span className="row-actions">
                  <label className="search">
                    <Icon name="search" size={17} strokeWidth={2} />
                    <span className="sr-only">Find a company</span>
                    <input type="search" placeholder="Find a company" value={q} onChange={(e) => setQ(e.target.value)} />
                  </label>
                  <SelectField label="Show" value={filter} onChange={(e) => setFilter(e.target.value)} options={FILTERS} />
                </span>
              }
            >
              {shown.length === 0 ? (
                <Empty title={filter || q ? 'No company matches' : 'Nothing archived yet'}>{filter || q ? 'Try another filter.' : 'Companies show here once anything of theirs is archived.'}</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">Company</th>
                        <th scope="col">Health</th>
                        <th scope="col">Where it goes</th>
                        <th scope="col">Waiting</th>
                        <th scope="col">Stitch’s copy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((c) => (
                        <tr key={c.customerId}>
                          <td>
                            <Link to={`/admin/customers/${encodeURIComponent(c.customerId)}`}>{c.name}</Link>
                            <div className="muted small">
                              {c.domain ?? 'No domain'}
                              {c.status === 'archive_only' ? ' · archive only' : ''}
                            </div>
                          </td>
                          <td>
                            <Chip tone={HEALTH[c.health].tone}>{HEALTH[c.health].label}</Chip>
                            {c.health !== 'ok' && <div className="small archive-said">{c.said}</div>}
                          </td>
                          <td>
                            <div>Stitch’s copy</div>
                            {c.own ? (
                              <div className="small">
                                + {PROVIDERS[c.own.provider] ?? c.own.provider}: {c.own.bucket} / {c.own.folder}
                                <div className="muted">
                                  {c.own.mode === 'sealed' ? 'Sealed' : 'Readable'}
                                  {c.own.backfill === 'running' ? ' · copying history' : ''}
                                  {c.own.lastWriteAt ? ` · written ${shortWhen(c.own.lastWriteAt)}` : ''}
                                  {c.own.keyExpiresAt ? ` · token ends ${dateOnly(c.own.keyExpiresAt)}` : ''}
                                </div>
                              </div>
                            ) : (
                              <div className="muted small">No storage of their own</div>
                            )}
                          </td>
                          <td className="mono">
                            {c.waiting.toLocaleString('en-US')}
                            {c.parked ? <span className="crit-text"> +{c.parked.toLocaleString('en-US')} stopped</span> : null}
                            {c.oldestWaiting && <div className="muted small">since {shortWhen(c.oldestWaiting)}</div>}
                            {c.own && c.own.waiting > 0 && <div className="muted small">{c.own.waiting.toLocaleString('en-US')} to their storage</div>}
                          </td>
                          <td>
                            {sizeOf(c.stitch.bytes)}, {plural(c.stitch.days, 'day')}
                            {c.stitch.lastWrittenAt && <div className="muted small">written {shortWhen(c.stitch.lastWrittenAt)}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card title="Deletion runs">
              <div className="card-body stack">
                <p className="muted small">Each company’s latest count of each kind with something in it: waiting for the company’s admin to confirm, confirmed and waiting, deleted, skipped or failed.</p>
                {v.deletions.length === 0 ? (
                  <p className="muted small">No counts with anything to delete yet.</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Company</th>
                          <th scope="col">Kind</th>
                          <th scope="col">From before</th>
                          <th scope="col">Count</th>
                          <th scope="col">State</th>
                        </tr>
                      </thead>
                      <tbody>
                        {v.deletions.map((d) => {
                          const st = deletionState(d);
                          return (
                            <tr key={d.runId}>
                              <td>{d.company}</td>
                              <td>{KINDS[d.kind] ?? d.kind}</td>
                              <td>{dateOnly(`${d.cutoff}T12:00:00Z`)}</td>
                              <td>
                                {d.items.toLocaleString('en-US')} ({sizeOf(d.bytes)})
                                {d.deleted && <div className="muted small">deleted {d.deleted.items.toLocaleString('en-US')}, {plural(d.deleted.files, 'file')}</div>}
                              </td>
                              <td>
                                <Chip tone={st.tone}>{st.label}</Chip>
                                {d.deleteNote && <div className="muted small">{d.deleteNote}</div>}
                                <div className="muted small">{shortWhen(d.deletedAt ?? d.at)}</div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>

            <Card title="Legal holds">
              <div className="card-body stack">
                <p className="muted small">Every hold that’s on, any company’s. While one is on, nothing it covers is deleted. What each is for stays with the hold (the company’s Legal holds page).</p>
                {v.holds.length === 0 ? (
                  <p className="muted small">No legal holds are on.</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Company</th>
                          <th scope="col">On</th>
                          <th scope="col">Days</th>
                          <th scope="col">Placed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {v.holds.map((h) => (
                          <tr key={h.id}>
                            <td>{h.company}</td>
                            <td>{h.on}</td>
                            <td>{h.fromDay || h.toDay ? `${h.fromDay ?? 'any'} to ${h.toDay ?? 'any'}` : 'Every day'}</td>
                            <td>
                              {h.placedByName || 'Someone'}
                              {h.byStaff ? (
                                <>
                                  {' '}
                                  <Chip small tone="brand">
                                    Stitch
                                  </Chip>
                                </>
                              ) : null}
                              <div className="muted small">{dateOnly(h.placedAt)}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>

            <Card title="Archive-only companies">
              <div className="card-body stack">
                <p className="muted small">Companies whose service ended and kept History, Storage and their legal holds. Their people’s last sign-in shows who still uses it.</p>
                {v.archiveOnly.length === 0 ? (
                  <p className="muted small">None.</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Company</th>
                          <th scope="col">Archive only since</th>
                          <th scope="col">Last sign-in</th>
                          <th scope="col">People</th>
                        </tr>
                      </thead>
                      <tbody>
                        {v.archiveOnly.map((a) => (
                          <tr key={a.customerId}>
                            <td>
                              <Link to={`/admin/customers/${encodeURIComponent(a.customerId)}`}>{a.name}</Link>
                              <div className="muted small">{a.domain ?? ''}</div>
                            </td>
                            <td>{a.since ? dateOnly(a.since) : '–'}</td>
                            <td>{a.lastSignInAt ? shortWhen(a.lastSignInAt) : 'Never'}</td>
                            <td>{a.people.toLocaleString('en-US')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>
            {v.companies.some((c) => c.health === 'failing') && (
              <Notice tone="warn">A company’s own storage that fails 5 times in a row emails its admins and Stitch support once; its copy catches up by itself when it answers. Stopped items go back in line with Try stuck items again on System → Archive.</Notice>
            )}
          </>
        )}
      </div>
    </>
  );
}
